/**
 * Query key factory for consistent cache key management.
 *
 * Pattern:
 * - ['provider'] - all provider queries
 * - ['provider', 'waitlists'] - waitlists list
 * - ['provider', 'waitlists', id] - single waitlist
 *
 * This structure enables:
 * - Granular invalidation: invalidate just one waitlist
 * - Broad invalidation: invalidate all provider data on logout
 */
export const queryKeys = {
  // ─────────────────────────────────────────────────────────────────
  // Provider domain
  // ─────────────────────────────────────────────────────────────────
  provider: {
    all: ['provider'] as const,

    // Portal state (subscription, signup status, etc.)
    portalState: () => [...queryKeys.provider.all, 'portal-state'] as const,

    // Profile
    profile: () => [...queryKeys.provider.all, 'profile'] as const,

    // Account (for nav/profile completion checking)
    account: () => [...queryKeys.provider.all, 'account'] as const,

    rewards: () => [...queryKeys.provider.all, 'rewards'] as const,

    invoices: () => [...queryKeys.provider.all, 'invoices'] as const,

    referrals: () => [...queryKeys.provider.all, 'referrals'] as const,

    websiteSync: {
      all: () => [...queryKeys.provider.all, 'website-sync'] as const,
      status: () =>
        [...queryKeys.provider.websiteSync.all(), 'status'] as const,
      pendingChanges: () =>
        [...queryKeys.provider.websiteSync.all(), 'pending-changes'] as const
    },

    messageTemplates: {
      all: () => [...queryKeys.provider.all, 'message-templates'] as const,
      list: () =>
        [...queryKeys.provider.messageTemplates.all(), 'list'] as const
    },

    nextStepTemplates: {
      all: () => [...queryKeys.provider.all, 'next-step-templates'] as const,
      list: () =>
        [...queryKeys.provider.nextStepTemplates.all(), 'list'] as const
    },

    applications: {
      all: () => [...queryKeys.provider.all, 'applications'] as const,
      list: () => [...queryKeys.provider.applications.all(), 'list'] as const,
      detail: (id: number | string) =>
        [...queryKeys.provider.applications.all(), 'detail', id] as const,
      syncRequest: () =>
        [...queryKeys.provider.applications.all(), 'sync-request'] as const,
      submissions: (filters?: Record<string, unknown>) =>
        [
          ...queryKeys.provider.applications.all(),
          'submissions',
          filters ?? {}
        ] as const
    },

    messageAssistQuota: () =>
      [...queryKeys.provider.all, 'message-assist-quota'] as const,

    notificationSettings: () =>
      [...queryKeys.provider.all, 'notification-settings'] as const,

    status: () => [...queryKeys.provider.all, 'status'] as const,

    // Waitlists
    waitlists: {
      all: () => [...queryKeys.provider.all, 'waitlists'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.provider.waitlists.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.provider.waitlists.all(), 'detail', id] as const,
      pendingRequestCount: () =>
        [
          ...queryKeys.provider.waitlists.all(),
          'pending-request-count'
        ] as const,
      pendingRequests: (limit: number) =>
        [
          ...queryKeys.provider.waitlists.all(),
          'pending-requests',
          limit
        ] as const
    },

    // Listings
    listings: {
      all: () => [...queryKeys.provider.all, 'listings'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.provider.listings.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.provider.listings.all(), 'detail', id] as const
    },

    // Litters
    litters: {
      all: () => [...queryKeys.provider.all, 'litters'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.provider.litters.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.provider.litters.all(), 'detail', id] as const
    },

    // Breeding dogs
    breedingDogs: {
      all: () => [...queryKeys.provider.all, 'breeding-dogs'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.provider.breedingDogs.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.provider.breedingDogs.all(), 'detail', id] as const
    },

    // Pups
    pups: {
      all: () => [...queryKeys.provider.all, 'pups'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.provider.pups.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.provider.pups.all(), 'detail', id] as const
    },

    // Messages/Conversations
    messages: {
      all: () => [...queryKeys.provider.all, 'messages'] as const,
      conversations: () =>
        [...queryKeys.provider.messages.all(), 'conversations'] as const,
      conversation: (id: number) =>
        [...queryKeys.provider.messages.all(), 'conversation', id] as const
    },

    connectionRequests: {
      all: () => [...queryKeys.provider.all, 'connection-requests'] as const,
      list: (filters?: Record<string, unknown>) =>
        [
          ...queryKeys.provider.connectionRequests.all(),
          'list',
          filters
        ] as const,
      count: () =>
        [...queryKeys.provider.connectionRequests.all(), 'count'] as const
    },

    // Reviews
    reviews: {
      all: () => [...queryKeys.provider.all, 'reviews'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.provider.reviews.all(), 'list', filters] as const
    },

    // Analytics
    analytics: {
      all: () => [...queryKeys.provider.all, 'analytics'] as const,
      overview: () =>
        [...queryKeys.provider.analytics.all(), 'overview'] as const,
      daily: (params?: { startDate?: string; endDate?: string }) =>
        [...queryKeys.provider.analytics.all(), 'daily', params] as const,
      pageViews: (days: number) =>
        [...queryKeys.provider.analytics.all(), 'page-views', days] as const,
      linkClicks: (days: number) =>
        [...queryKeys.provider.analytics.all(), 'link-clicks', days] as const
    },

    // Media library
    media: {
      all: () => [...queryKeys.provider.all, 'media'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.provider.media.all(), 'list', filters] as const,
      gallery: () => [...queryKeys.provider.media.all(), 'gallery'] as const,
      gallerySettings: () =>
        [...queryKeys.provider.media.all(), 'gallery-settings'] as const
    },

    // Leads automation
    leadsAutomation: {
      all: () => [...queryKeys.provider.all, 'leads-automation'] as const,
      config: () =>
        [...queryKeys.provider.leadsAutomation.all(), 'config'] as const
    },

    // Files
    files: {
      all: () => [...queryKeys.provider.all, 'files'] as const,
      list: () => [...queryKeys.provider.files.all(), 'list'] as const
    },

    // Payments
    payments: {
      all: () => [...queryKeys.provider.all, 'payments'] as const,
      deposits: () =>
        [...queryKeys.provider.payments.all(), 'deposits'] as const,
      purchases: () =>
        [...queryKeys.provider.payments.all(), 'purchases'] as const
    },

    // Requests (listing/waitlist requests)
    requests: {
      all: () => [...queryKeys.provider.all, 'requests'] as const,
      listing: () => [...queryKeys.provider.requests.all(), 'listing'] as const,
      pendingCount: () =>
        [...queryKeys.provider.requests.all(), 'pending-count'] as const,
      pendingList: (limit: number) =>
        [...queryKeys.provider.requests.all(), 'pending-list', limit] as const,
      waitlist: (waitlistId?: number | string) =>
        [...queryKeys.provider.requests.all(), 'waitlist', waitlistId] as const
    },

    // Visits (bookings)
    visits: {
      all: () => [...queryKeys.provider.all, 'visits'] as const,
      upcoming: () => [...queryKeys.provider.visits.all(), 'upcoming'] as const
    },

    // Articles (marketing blog posts)
    articles: () => [...queryKeys.provider.all, 'articles'] as const
  },

  // ─────────────────────────────────────────────────────────────────
  // Purchaser domain
  // ─────────────────────────────────────────────────────────────────
  purchaser: {
    all: ['purchaser'] as const,

    // Profile
    profile: () => [...queryKeys.purchaser.all, 'profile'] as const,

    // Dashboard
    dashboard: () => [...queryKeys.purchaser.all, 'dashboard'] as const,

    applications: {
      all: () => [...queryKeys.purchaser.all, 'applications'] as const,
      list: () => [...queryKeys.purchaser.applications.all(), 'list'] as const,
      detail: (id: number | string) =>
        [...queryKeys.purchaser.applications.all(), 'detail', id] as const
    },

    handbooks: {
      all: () => [...queryKeys.purchaser.all, 'handbooks'] as const,
      list: () => [...queryKeys.purchaser.handbooks.all(), 'list'] as const,
      breeds: () => [...queryKeys.purchaser.handbooks.all(), 'breeds'] as const,
      pastBreedOptions: () =>
        [...queryKeys.purchaser.handbooks.all(), 'pastBreedOptions'] as const,
      detail: (breedSlug: string) =>
        [...queryKeys.purchaser.handbooks.all(), 'detail', breedSlug] as const
    },

    // Research (saved searches, favorites, etc.)
    research: {
      all: () => [...queryKeys.purchaser.all, 'research'] as const,
      favorites: () =>
        [...queryKeys.purchaser.research.all(), 'favorites'] as const,
      savedSearches: () =>
        [...queryKeys.purchaser.research.all(), 'saved-searches'] as const
    },

    // Visits
    visits: {
      all: () => [...queryKeys.purchaser.all, 'visits'] as const,
      list: () => [...queryKeys.purchaser.visits.all(), 'list'] as const,
      detail: (id: number | string) =>
        [...queryKeys.purchaser.visits.all(), 'detail', id] as const
    },

    // Pups (owned/purchased)
    pups: {
      all: () => [...queryKeys.purchaser.all, 'pups'] as const,
      list: () => [...queryKeys.purchaser.pups.all(), 'list'] as const,
      detail: (id: number | string) =>
        [...queryKeys.purchaser.pups.all(), 'detail', id] as const
    },

    // Messages
    messages: {
      all: () => [...queryKeys.purchaser.all, 'messages'] as const,
      conversations: () =>
        [...queryKeys.purchaser.messages.all(), 'conversations'] as const,
      archivedConversations: () =>
        [
          ...queryKeys.purchaser.messages.all(),
          'conversations',
          'archived'
        ] as const,
      conversation: (id: number) =>
        [...queryKeys.purchaser.messages.all(), 'conversation', id] as const,
      conversationListing: (id: number) =>
        [
          ...queryKeys.purchaser.messages.all(),
          'conversation',
          id,
          'listing'
        ] as const,
      search: (query: string) =>
        [...queryKeys.purchaser.messages.all(), 'search', query] as const,
      providerStats: (conversationId: number) =>
        [
          ...queryKeys.purchaser.messages.all(),
          'provider-stats',
          conversationId
        ] as const,
      quickReplyPrompts: (conversationId: number) =>
        [
          ...queryKeys.purchaser.messages.all(),
          'quick-reply-prompts',
          conversationId
        ] as const,
      myStats: () =>
        [...queryKeys.purchaser.messages.all(), 'my-stats'] as const,
      myAnalytics: (days: number) =>
        [...queryKeys.purchaser.messages.all(), 'my-analytics', days] as const
    },

    // Waitlist entries
    waitlistEntries: {
      all: () => [...queryKeys.purchaser.all, 'waitlist-entries'] as const,
      list: () =>
        [...queryKeys.purchaser.waitlistEntries.all(), 'list'] as const
    },

    // Notifications
    notifications: () => [...queryKeys.purchaser.all, 'notifications'] as const
  },

  // ─────────────────────────────────────────────────────────────────
  // Admin domain
  // ─────────────────────────────────────────────────────────────────
  admin: {
    all: ['admin'] as const,

    // Dashboard
    dashboard: () => [...queryKeys.admin.all, 'dashboard'] as const,

    // Users
    users: {
      all: () => [...queryKeys.admin.all, 'users'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.users.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.admin.users.all(), 'detail', id] as const
    },

    // Providers
    providers: {
      all: () => [...queryKeys.admin.all, 'providers'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.providers.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.admin.providers.all(), 'detail', id] as const,
      incomplete: () =>
        [...queryKeys.admin.providers.all(), 'incomplete'] as const
    },

    // Accounts (purchasers)
    accounts: {
      all: () => [...queryKeys.admin.all, 'accounts'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.accounts.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.admin.accounts.all(), 'detail', id] as const
    },

    // Analytics
    analytics: {
      all: () => [...queryKeys.admin.all, 'analytics'] as const,
      overview: () => [...queryKeys.admin.analytics.all(), 'overview'] as const,
      funnel: (params?: Record<string, unknown>) =>
        [...queryKeys.admin.analytics.all(), 'funnel', params] as const
    },

    // Content management
    content: {
      all: () => [...queryKeys.admin.all, 'content'] as const,
      breeds: {
        all: () => [...queryKeys.admin.content.all(), 'breeds'] as const,
        list: () => [...queryKeys.admin.content.breeds.all(), 'list'] as const,
        detail: (id: number | string) =>
          [...queryKeys.admin.content.breeds.all(), 'detail', id] as const
      },
      articles: {
        all: () => [...queryKeys.admin.content.all(), 'articles'] as const,
        list: () =>
          [...queryKeys.admin.content.articles.all(), 'list'] as const,
        detail: (id: number | string) =>
          [...queryKeys.admin.content.articles.all(), 'detail', id] as const
      },
      faqs: {
        all: () => [...queryKeys.admin.content.all(), 'faqs'] as const,
        list: () => [...queryKeys.admin.content.faqs.all(), 'list'] as const
      },
      picks: {
        all: () => [...queryKeys.admin.content.all(), 'picks'] as const,
        list: () => [...queryKeys.admin.content.picks.all(), 'list'] as const
      }
    },

    // Metadata
    metadata: {
      all: () => [...queryKeys.admin.all, 'metadata'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.metadata.all(), 'list', filters] as const,
      detail: (id: number | string) =>
        [...queryKeys.admin.metadata.all(), 'detail', id] as const
    },

    // Operations
    operations: {
      all: () => [...queryKeys.admin.all, 'operations'] as const,
      leads: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.operations.all(), 'leads', filters] as const,
      smsCampaigns: () =>
        [...queryKeys.admin.operations.all(), 'sms-campaigns'] as const,
      emailLogs: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.operations.all(), 'email-logs', filters] as const
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // Account (cross-domain user data)
  // ─────────────────────────────────────────────────────────────────
  account: {
    all: ['account'] as const,

    // User profile
    profile: () => ['account', 'profile'] as const,

    // Notifications
    notifications: () => ['account', 'notifications'] as const,

    mobileApp: () => ['account', 'mobileApp'] as const,

    // Conversations/Messages
    conversations: {
      all: () => ['account', 'conversations'] as const,
      list: () => ['account', 'conversations', 'list'] as const,
      detail: (id: string) =>
        ['account', 'conversations', 'detail', id] as const,
      messages: (id: string) =>
        ['account', 'conversations', id, 'messages'] as const
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // Shared/common
  // ─────────────────────────────────────────────────────────────────
  shared: {
    // Current user session
    session: () => ['session'] as const,

    // Shared files
    files: {
      all: () => ['shared-files'] as const,
      list: () => ['shared-files', 'list'] as const
    }
  },

  // ─────────────────────────────────────────────────────────────────
  // Public catalog (SSR pages like /puppies, /breeders)
  // Used for SSR prefetching and client-side cache hydration
  // ─────────────────────────────────────────────────────────────────
  catalog: {
    all: ['catalog'] as const,

    // Provider listings (puppies page)
    providers: (filters?: Record<string, unknown>) =>
      ['catalog', 'providers', filters] as const,

    // Single provider profile (breeder page)
    providerProfile: (slug: string) => ['catalog', 'provider', slug] as const,

    // Breed info pages
    breeds: {
      all: () => ['catalog', 'breeds'] as const,
      list: () => ['catalog', 'breeds', 'list'] as const,
      detail: (slug: string) => ['catalog', 'breeds', slug] as const
    },

    // Locations (e.g., /puppies-for-sale-in/texas)
    locations: {
      all: () => ['catalog', 'locations'] as const,
      state: (stateSlug: string) =>
        ['catalog', 'locations', 'state', stateSlug] as const,
      city: (stateSlug: string, citySlug: string) =>
        ['catalog', 'locations', 'city', stateSlug, citySlug] as const
    },

    // Search suggestions / autocomplete
    suggestions: (query: string) => ['catalog', 'suggestions', query] as const
  }
} as const;

export type QueryKeys = typeof queryKeys;
